/**
 * API client with authentication interceptors.
 */
import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { getApiUrl } from '../config/runtime';

const API_URL = getApiUrl();

// Create axios instance
// SEC-003: Use withCredentials to send httpOnly cookies with all requests
const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // SEC-003: Send httpOnly cookies automatically
});

// SEC-003: No longer need to add Authorization header - cookies are sent automatically
// Request interceptor removed - httpOnly cookies are handled by browser automatically

// Response interceptor to handle auth errors and CSRF token errors
// SEC-003: Cookies are cleared by backend on logout, no need to clear localStorage
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // CODE-007: Handle CSRF token errors by clearing cache and retrying once
    if (error.response?.status === 403) {
      const errorDetail = error.response?.data?.detail;
      if (errorDetail === 'Invalid CSRF token' || errorDetail?.includes('CSRF')) {
        // Clear the stale CSRF token cache
        const { clearCsrfToken } = await import('./csrf');
        clearCsrfToken();
        
        // Retry the request once with a fresh CSRF token
        // Only retry if this is a state-changing request (PUT, POST, DELETE)
        const method = error.config?.method?.toUpperCase();
        if (method && ['PUT', 'POST', 'DELETE'].includes(method)) {
          try {
            // Get fresh CSRF token and retry the request
            const { getCsrfHeader } = await import('./csrf');
            const csrfHeaders = await getCsrfHeader();
            error.config.headers = {
              ...error.config.headers,
              ...csrfHeaders,
            };
            return apiClient.request(error.config);
          } catch (retryError) {
            // If retry fails, reject with original error
            return Promise.reject(error);
          }
        }
      }
    }
    
    if (error.response?.status === 401) {
      // Don't redirect if this is a login request - let the login page handle the error
      const isLoginRequest = error.config?.url?.includes('/api/auth/login');
      
      if (!isLoginRequest) {
        // Unauthorized on protected endpoints - redirect to login
        // SEC-003: No need to clear localStorage - cookies are handled by backend
        // Only redirect if we're not already on the login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;

